import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import { v4 as uuidv4 } from 'uuid';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import {
  addTranscriptionText,
  addRawTranscript,
  addCorrectedTranscript,
  addDeferredRawToCorrected,
  setFinalRawText,
  setSessionId,
  setIsFinal,
  setClassification,
  setRefiningInFlight,
  setCorrectionFailed,
} from '../store/transcriptionSlice';
import { WS_URL } from '../config';
import { store } from '../store';
import { grantAllConsent } from '../services/consentService';

const TARGET_SAMPLE_RATE = 16000;

/**
 * Down-sample a Float32Array captured at `inputRate` to `outputRate`
 * using simple linear interpolation.
 */
function downsampleBuffer(buffer: Float32Array, inputRate: number, outputRate: number): Float32Array {
  if (inputRate === outputRate) return buffer;
  const ratio = inputRate / outputRate;
  const newLength = Math.round(buffer.length / ratio);
  const result = new Float32Array(newLength);
  for (let i = 0; i < newLength; i++) {
    const idx = i * ratio;
    const low = Math.floor(idx);
    const high = Math.min(low + 1, buffer.length - 1);
    const frac = idx - low;
    result[i] = buffer[low] * (1 - frac) + buffer[high] * frac;
  }
  return result;
}

/** Convert Float32 samples (–1…+1) to PCM 16-bit little-endian bytes. */
function float32ToPcm16(samples: Float32Array): ArrayBuffer {
  const buf = new ArrayBuffer(samples.length * 2);
  const view = new DataView(buf);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buf;
}

/** Convert an ArrayBuffer to a base64 string. */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

interface AudioRecorderProps {
  onTranscriptionStart: () => void;
  onTranscriptionEnd: () => void;
  /** Called immediately when stop is initiated, before finalization completes */
  onTranscriptionStopping?: () => void;
  existingSessionId?: string | null;
  patientId?: string | null;
  autoStart?: boolean;
  stopTrigger?: number; // Increment to trigger stop from parent
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({
  onTranscriptionStart,
  onTranscriptionEnd,
  onTranscriptionStopping,
  existingSessionId,
  patientId,
  autoStart = false,
  stopTrigger = 0,
}) => {
  const deferredCorrectionEnabled = useAppSelector((state) => state.transcription.deferredCorrectionEnabled);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // ``isFinalizing`` drives the "Finalizing…" indicator that is shown after the
  // user clicks Stop while we wait for the backend's final-pass refinement to
  // complete (review_refining_status with in_flight=false). It mirrors
  // pendingStopRef so React can render UI off it; the ref remains the
  // authoritative state used inside callbacks/handlers.
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const socketRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const sessionIdRef = useRef<string>('');
  const chunkCountRef = useRef(0);
  const consentReadyRef = useRef(false);
  const patientIdRef = useRef<string | null>(null);
  const pendingStopRef = useRef(false);
  const pendingStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Single-shot guard: ensures finalizeStop's UI side-effects (setIsFinal +
  // onTranscriptionEnd) run exactly once per WS session, even if multiple
  // termination paths fire (onclose + watchdog + explicit forceFinalizeNow).
  const finalizedRef = useRef(false);
  // Tracks whether the backend's final-pass transcript_update (is_final=true with
  // corrected text) has been received. Without this, an in-flight incremental
  // correction that completes after Stop (sending review_refining_status
  // {in_flight:false}) could trigger premature finalisation before the
  // definitive final-pass correction arrives.
  const receivedFinalTranscriptRef = useRef(false);
  // Watchdog: max time we wait for backend to flush final processing before forcing finalize.
  const PENDING_STOP_TIMEOUT_MS = 30000;
  
  const dispatch = useAppDispatch();

  const clearPendingStopTimer = useCallback(() => {
    if (pendingStopTimerRef.current) {
      clearTimeout(pendingStopTimerRef.current);
      pendingStopTimerRef.current = null;
    }
  }, []);

  const finalizeStop = useCallback(() => {
    // Single-shot: defend against double-fire from onclose + watchdog + explicit caller.
    if (finalizedRef.current) {
      return;
    }
    finalizedRef.current = true;
    pendingStopRef.current = false;
    setIsFinalizing(false);
    clearPendingStopTimer();
    // Close the WebSocket so no further server messages are dispatched after finalization.
    if (socketRef.current) {
      // Disable handlers BEFORE close() to prevent re-entry via onclose/onerror.
      socketRef.current.onclose = null;
      socketRef.current.onerror = null;
      socketRef.current.onmessage = null;
      if (socketRef.current.readyState !== WebSocket.CLOSED) {
        socketRef.current.close();
      }
      socketRef.current = null;
    }
    // Mark transcription as final to enable UI actions (Generate Report, Save, History)
    dispatch(setIsFinal(true));
    onTranscriptionEnd();
  }, [onTranscriptionEnd, dispatch, clearPendingStopTimer]);

  // ``sendFinal=false`` skips the {is_final: true} WS message — used for terminal
  // error paths (consent rejection, transport error) where awaiting a backend final
  // pass is pointless because the server is already tearing the session down.
  const stopRecording = useCallback((sendFinal = true) => {
    // Re-entry guard: ignore subsequent invocations once a stop is in-flight or
    // the session has already been finalized. Without this, the user clicking
    // Stop twice (or auto-stop racing a user click, or watchdog racing onclose)
    // would (a) send duplicate {is_final:true} frames, (b) reset the watchdog
    // and effectively extend the stall window, and (c) tear down audio refs
    // that are already null. Audio teardown below is also idempotent, but
    // returning early avoids the misleading log line and watchdog churn.
    if (pendingStopRef.current || finalizedRef.current) {
      console.debug(
        `[Audio] stopRecording ignored — already ${finalizedRef.current ? 'finalized' : 'pending stop'}`,
      );
      return;
    }

    // Notify parent immediately that stop has been initiated for responsive UI
    onTranscriptionStopping?.();

    // Disconnect audio pipeline
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    console.log(`[Audio] Recording stopped after ${chunkCountRef.current} chunks`);
    chunkCountRef.current = 0;
    setIsRecording(false);
    setIsProcessing(false);

    // Send final signal and wait for server processing to complete
    if (sendFinal && socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      pendingStopRef.current = true;
      setIsFinalizing(true);
      socketRef.current.send(JSON.stringify({ is_final: true }));
      console.log('[WS] Sent final signal to server, waiting for processing to complete...');
      // Watchdog: force-finalize if backend never emits in_flight=false (LLM hang, network black-hole).
      clearPendingStopTimer();
      pendingStopTimerRef.current = setTimeout(() => {
        if (pendingStopRef.current) {
          console.warn(`[WS] Pending-stop watchdog fired after ${PENDING_STOP_TIMEOUT_MS}ms — forcing finalize`);
          finalizeStop();
        }
      }, PENDING_STOP_TIMEOUT_MS);
    } else {
      if (sendFinal) {
        console.warn('[WS] Could not send final signal — WebSocket not open');
      }
      // No backend roundtrip to await — finalize immediately.
      finalizeStop();
    }
  }, [finalizeStop, clearPendingStopTimer]);

  // Force-finalize without waiting on backend. Two callers:
  //   1. Terminal error paths (consent rejection, transport-level WS error)
  //      where the backend will not deliver a final review_refining_status \u2014
  //      audio may still be live, so tear it down before closing the WS.
  //   2. The "Skip & finalize now" button surfaced by the Finalizing\u2026 UI \u2014
  //      pendingStopRef is already true and audio is already torn down, so
  //      we must bypass stopRecording's re-entry guard and call finalizeStop
  //      directly. ``finalizeStop`` itself disables WS handlers and closes
  //      the socket, so no {is_final:true} frame is sent.
  const forceFinalizeNow = useCallback(() => {
    if (finalizedRef.current) return;
    if (pendingStopRef.current) {
      // Manual-override / late-error path: audio already disconnected, skip
      // straight to finalize so the watchdog and pending wait are short-circuited.
      finalizeStop();
      return;
    }
    // Early error path: audio is still live, do the full teardown via
    // stopRecording's else branch (which finalises immediately when sendFinal=false).
    stopRecording(false);
  }, [stopRecording, finalizeStop]);
  
  // Track if we've already auto-started to prevent re-triggering.
  // Reset when autoStart flips false→true so a subsequent continue-recording session
  // (which sets autoStart=true again after it was false) can trigger a new auto-start.
  const hasAutoStartedRef = useRef(false);
  const prevAutoStartRef = useRef(autoStart);

  useEffect(() => {
    if (!prevAutoStartRef.current && autoStart) {
      // autoStart just became true — reset guard so this new session can fire
      hasAutoStartedRef.current = false;
    }
    prevAutoStartRef.current = autoStart;

    if (autoStart && !hasAutoStartedRef.current && !isRecording && !isProcessing) {
      hasAutoStartedRef.current = true;
      startRecording();
    }
  }, [autoStart, isRecording, isProcessing]);

  // Handle stop trigger from parent (toolbar stop button).
  // isRecording and stopRecording are intentionally read via refs to avoid
  // the effect re-registering on every recording state change — the trigger
  // value is the only meaningful dependency.
  const isRecordingRef = useRef(isRecording);
  isRecordingRef.current = isRecording;
  const stopRecordingRef = useRef(stopRecording);
  stopRecordingRef.current = stopRecording;
  const lastStopTriggerRef = useRef(stopTrigger);
  useEffect(() => {
    if (stopTrigger > 0 && stopTrigger !== lastStopTriggerRef.current) {
      lastStopTriggerRef.current = stopTrigger;
      if (isRecordingRef.current) {
        stopRecordingRef.current(true);
      }
    }
  // stopTrigger is the only meaningful trigger; latest isRecording/stopRecording
  // are captured via refs so the effect never re-registers on state changes.
  }, [stopTrigger]);

  useEffect(() => {
    // Clean up on component unmount
    return () => {
      clearPendingStopTimer();
      // Always clear the finalizing UI state on unmount to prevent stuck UI
      // if the component unmounts during finalization.
      setIsFinalizing(false);
      // If a stop was in-flight when the component unmounts, dispatch the same
      // parent-state reset that finalizeStop would have performed, so global
      // Redux flags (isFinal, isRefiningInFlight) cannot stay stuck if the user
      // navigates away mid-finalization. We deliberately do NOT call
      // onTranscriptionEnd here — the parent component owning that callback is
      // unmounting alongside us, and invoking it would risk a setState on an
      // unmounted component.
      if (pendingStopRef.current && !finalizedRef.current) {
        finalizedRef.current = true;
        pendingStopRef.current = false;
        try {
          dispatch(setIsFinal(true));
          dispatch(setRefiningInFlight(false));
        } catch (dispatchErr) {
          // Defensive: dispatch can throw if the store has already been torn
          // down (e.g. test teardown). Log but don't throw so unmount cleanup completes.
          console.error('[AudioRecorder] CRITICAL: unmount-finalize dispatch failed:', dispatchErr);
          console.error('[AudioRecorder] Redux state may be stuck: isFinal=false, isRefiningInFlight=true');
        }
      }
      if (socketRef.current) {
        socketRef.current.onclose = null;
        socketRef.current.onerror = null;
        socketRef.current.onmessage = null;
        if (socketRef.current.readyState === WebSocket.OPEN) {
          socketRef.current.close();
        }
        socketRef.current = null;
      }
    };
  }, [clearPendingStopTimer, dispatch]);
  
  const initializeWebSocket = (consentGranted: boolean) => {
    const sessionId = sessionIdRef.current;
    
    const token = store.getState().auth.token;
    const wsUrl = `${WS_URL}/api/v1/ws/transcribe/${sessionId}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    console.log(`[WS] Connecting to ${wsUrl.replace(/token=[^&]+/, 'token=***')}`);
    socketRef.current = new WebSocket(wsUrl);
    
    socketRef.current.onopen = () => {
      console.log(`[WS] Connected to session ${sessionId}`);
      if (consentGranted && socketRef.current) {
        // Send consent handshake as the first message
        const handshake = JSON.stringify({
          type: 'consent_handshake',
          patient_id: patientIdRef.current || sessionId,
        });
        socketRef.current.send(handshake);
        console.log('[WS] Sent consent handshake');
      } else {
        // No consent required — audio can start immediately
        consentReadyRef.current = true;
      }
    };
    
    socketRef.current.onmessage = (event) => {
      // Guard against executing after finalizeStop has already run (handlers are nulled but
      // already-scheduled microtasks may still fire). finalizedRef is the single source of truth.
      if (finalizedRef.current) {
        return;
      }
      try {
        const data = JSON.parse(event.data);

        // Handle consent acknowledgement — ungate audio streaming
        if (data.type === 'consent_acknowledged') {
          console.log(`[WS] Consent acknowledged for patient=${data.patient_id}`);
          consentReadyRef.current = true;
          return;
        }

        // Handle consent / handshake errors from the server
        if (data.type === 'error' && (data.code === 'CONSENT_REQUIRED' || data.code === 'CONSENT_HANDSHAKE_INVALID' || data.code === 'CONSENT_HANDSHAKE_MISSING')) {
          console.error(`[WS] Consent error: ${data.code} — ${data.message}`);
          setError(`Consent required: ${data.message}`);
          // Consent rejection: server is tearing the WS down — no final-pass
          // refinement will run. Teardown audio and finalize without sending {is_final:true}.
          stopRecording(false);
          return;
        }

        if (data.error) {
          console.error(`[WS] Server error for session ${sessionId}:`, data.error);
          setError(data.error);
          // Ensure audio teardown before finalization
          stopRecording(false);
          return;
        }

        // Handle classification result from backend
        if (data.type === 'classification_update') {
          console.debug(`[WS] Classification: ${data.category}/${data.pathology_type} (confidence=${data.confidence})`);
          dispatch(setClassification({
            category: data.category,
            pathologyType: data.pathology_type,
            confidence: data.confidence,
          }));
          return;
        }

        if (data.type === 'review_refining_status') {
          dispatch(setRefiningInFlight(Boolean(data.in_flight)));
          // If we're waiting to stop and processing is now complete, finalize —
          // but ONLY if we've already received the definitive final-pass
          // transcript_update.  An in-flight incremental correction that finishes
          // after Stop sends in_flight=false before the backend's final pass
          // runs; finalising on that would drop the final corrected text.
          if (pendingStopRef.current && !data.in_flight && receivedFinalTranscriptRef.current) {
            console.log('[WS] Final processing complete and final transcript received, finalizing stop...');
            finalizeStop();
          }
          return;
        }

        // Handle auto-stop: backend detected inactivity and stopped streaming.
        // The server STILL runs run_final_pass() in its `finally` block and emits
        // a final transcript_update + review_refining_status, so we must NOT
        // force-close the socket here — let stopRecording's pending-stop wait for
        // those messages (the watchdog will force-finalize if they never arrive).
        if (data.type === 'dictation_auto_stopped') {
          console.log(`[WS] Auto-stop received for session ${sessionId} (state=${data.state})`);
          stopRecording();
          return;
        }

        // Handle resume after auto-stop (future use)
        if (data.type === 'dictation_resumed') {
          console.log(`[WS] Dictation resumed for session ${sessionId} (state=${data.state})`);
          return;
        }

        // DEPRECATED: correction_patch is part of the legacy real-time patch pipeline
        // (ENABLE_CORRECTION_PATCH + REFINEMENT_CHARACTER_COUNT_ENABLED). The default
        // flow uses ENABLE_DEFERRED_CORRECTION=true which never emits this message type.
        // Left in place only for legacy testing when deferred correction is explicitly disabled.
        if (data.type === 'correction_patch') {
          console.debug(
            `[WS] [DEPRECATED] correction_patch received but ignored — ` +
            `use ENABLE_DEFERRED_CORRECTION=true (default). chunk_id=${data.chunk_id}`
          );
          return;
        }

        // Handle new dual-stream payload: { type, raw, corrected, is_final, is_incremental }
        if (data.type === 'transcript_update') {
          if (data.corrected) {
            // In deferred mode, ignore incremental LLM corrections (raw text is shown instead).
            // Only accept the final correction when the recording stops.
            if (deferredCorrectionEnabled && data.is_incremental && !data.is_final) {
              console.debug('[WS] Skipping incremental correction in deferred mode');
              return;
            }
            // LLM-corrected transcript arrived (async, after raw was already shown)
            console.debug(`[WS] Corrected transcript (incremental=${data.is_incremental}): "${data.corrected?.substring(0, 80)}"`);
            dispatch(addCorrectedTranscript({
              raw: data.raw,
              corrected: data.corrected,
              isIncremental: data.is_incremental ?? false,
              isFinal: data.is_final ?? false,
              fallback: data.fallback ?? false,
            }));
            if (data.fallback) {
              dispatch(setCorrectionFailed(true));
            }
            // On the final pass, replace the raw text with the backend's authoritative
            // snapshot so DICTATION CAPTURE matches the input that was actually corrected
            // (drops any trailing STT partial that never received an end_of_turn).
            if (data.is_final && data.raw) {
              dispatch(setFinalRawText(data.raw));
            }
            // Mark that the definitive final-pass transcript has arrived.
            // If refining is already idle (in_flight was set to false by an
            // earlier incremental completion), finalize now.
            if (data.is_final) {
              receivedFinalTranscriptRef.current = true;
              if (pendingStopRef.current && !store.getState().transcription.isRefiningInFlight) {
                console.log('[WS] Final transcript received and refining idle, finalizing stop...');
                finalizeStop();
              }
            }
          }

          if (data.raw && !data.corrected) {
            // Raw ASR transcript — display immediately (only when this is NOT a correction-only message)
            console.debug(`[WS] Raw transcript (final=${data.is_final}): "${data.raw?.substring(0, 80)}"`);
            dispatch(addRawTranscript({ text: data.raw, isFinal: data.is_final }));
            // Also update the legacy text field for backward compat
            dispatch(addTranscriptionText({ text: data.raw, isFinal: data.is_final }));
            // In deferred correction mode, mirror raw text into correctedText so the
            // Reviewed Dictation section shows live STT output during recording.
            // Use dedicated action with simple accumulation (same logic as addRawTranscript).
            if (deferredCorrectionEnabled) {
              dispatch(addDeferredRawToCorrected({
                text: data.raw,
                isFinal: data.is_final ?? false,
              }));
            }
          }
        } else {
          // Backward-compatible fallback for old payload shape
          console.debug(`[WS] Transcript (final=${data.is_final}): "${data.text?.substring(0, 80)}"`);
          dispatch(addTranscriptionText({ 
            text: data.text, 
            isFinal: data.is_final 
          }));
        }
      } catch (parseErr) {
        console.error('[WS] Failed to parse server message:', parseErr, event.data);
      }
    };
    
    socketRef.current.onerror = (wsError) => {
      // Guard against executing after finalizeStop has already run (handlers are nulled but
      // already-scheduled microtasks may still fire). finalizedRef is the single source of truth.
      if (finalizedRef.current) {
        return;
      }
      console.error(`[WS] Connection error for session ${sessionId} (url=${wsUrl.replace(/token=[^&]+/, 'token=***')}):`, wsError);
      setError(`Connection error (${wsUrl.split('?')[0]}). Please try again.`);
      // Network/transport error — backend will not deliver further status messages.
      // Ensure audio teardown before finalization.
      stopRecording(false);
    };
    
    socketRef.current.onclose = (event) => {
      // Guard against executing after finalizeStop has already run (handlers are nulled but
      // already-scheduled microtasks may still fire). finalizedRef is the single source of truth.
      if (finalizedRef.current) {
        return;
      }
      console.log(`[WS] Disconnected from session ${sessionId} (code=${event.code}, reason=${event.reason || 'none'}, wasClean=${event.wasClean})`);
      // Handle server-initiated rejection codes before normal close handling
      if (event.code === 4004) {
        setError('Cannot add dictation: this report is finalized and locked.');
        stopRecording(false);
        return;
      }
      // If we were waiting for final processing and connection closed, finalize immediately
      if (pendingStopRef.current && !finalizedRef.current) {
        if (!event.wasClean) {
          console.warn('[WS] Unclean close during pending stop — backend processing may be incomplete');
        }
        console.log('[WS] Connection closed while waiting for final processing, finalizing stop...');
        finalizeStop();
      }
    };
  };
  
  const startRecording = async () => {
    try {
      setError(null);
      setIsProcessing(true);
      pendingStopRef.current = false;
      // Reset single-shot finalize guard so a new session can be finalized later
      // (the same AudioRecorder instance is reused across continue/append flows).
      finalizedRef.current = false;
      receivedFinalTranscriptRef.current = false;
      
      console.log('[Audio] Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: TARGET_SAMPLE_RATE,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      streamRef.current = stream;
      console.log('[Audio] Microphone access granted, tracks:', stream.getAudioTracks().map(t => t.label));
      // Reset any lingering Finalizing… UI from a prior session.
      setIsFinalizing(false);

      // Generate session ID and persist consent BEFORE opening WS
      const sessionId = existingSessionId || uuidv4();
      sessionIdRef.current = sessionId;
      dispatch(setSessionId(sessionId));
      const effectivePatientId = patientId || sessionId;
      patientIdRef.current = effectivePatientId;

      // Grant consent via REST API before WS connect
      let consentGranted = false;
      try {
        await grantAllConsent(effectivePatientId, sessionId);
        consentGranted = true;
        console.log(`[Consent] All consents granted for patient=${effectivePatientId} session=${sessionId}`);
      } catch (consentErr: unknown) {
        // If consent API fails (e.g. 404 when consent routes not mounted, or
        // REQUIRE_PATIENT_CONSENT is disabled), proceed without consent.
        // The WS handler will also skip consent validation when the flag is off.
        console.warn('[Consent] grant-all failed, proceeding without consent:', consentErr);
      }

      consentReadyRef.current = false;
      initializeWebSocket(consentGranted);

      // Use Web Audio API to capture raw PCM16 audio
      const audioContext = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      sourceRef.current = source;

      // ScriptProcessorNode with 1024-sample buffer (~64ms at 16kHz)
      const processor = audioContext.createScriptProcessor(1024, 1, 1);
      processorRef.current = processor;

      let chunkCount = 0;
      processor.onaudioprocess = (e: AudioProcessingEvent) => {
        const inputData = e.inputBuffer.getChannelData(0);

        // Down-sample if the AudioContext didn't honour our requested rate
        const pcmFloat = audioContext.sampleRate !== TARGET_SAMPLE_RATE
          ? downsampleBuffer(inputData, audioContext.sampleRate, TARGET_SAMPLE_RATE)
          : new Float32Array(inputData);

        const pcm16 = float32ToPcm16(pcmFloat);
        const base64data = arrayBufferToBase64(pcm16);

        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN && consentReadyRef.current) {
          socketRef.current.send(JSON.stringify({
            audio_data: base64data,
            is_final: false,
          }));
          chunkCount++;
          chunkCountRef.current = chunkCount;
          if (chunkCount % 20 === 0) {
            console.debug(`[Audio] Sent ${chunkCount} PCM16 chunks (${pcm16.byteLength} bytes each)`);
          }
        } else if (socketRef.current) {
          console.warn(`[Audio] WebSocket not open (state=${socketRef.current.readyState}), dropping chunk #${chunkCount}`);
        }
      };

      source.connect(processor);
      processor.connect(audioContext.destination);
      
      setIsRecording(true);
      setIsProcessing(false);
      onTranscriptionStart();
    } catch (err) {
      console.error('[Audio] Failed to start recording:', err);
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone access in your browser settings.');
      } else if (err instanceof DOMException && err.name === 'NotFoundError') {
        setError('No microphone detected. Please connect a microphone and try again.');
      } else {
        setError('Could not access microphone. Please check your device permissions.');
      }
      setIsProcessing(false);
    }
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}
      
      {!isRecording ? (
        <Button
          variant="contained"
          color="primary"
          startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <MicIcon />}
          onClick={startRecording}
          disabled={isProcessing}
          size="small"
          fullWidth
          aria-label={isProcessing ? 'Preparing recording' : 'Start recording'}
        >
          {isProcessing ? 'Preparing...' : 'Start Dictation'}
        </Button>
      ) : (
        <Button
          variant="contained"
          color="error"
          startIcon={<StopIcon />}
          onClick={() => stopRecording()}
          size="small"
          fullWidth
          aria-label={isFinalizing ? 'Stopping recording' : 'Stop recording'}
          disabled={isFinalizing}
        >
          Stop Dictation
        </Button>
      )}

      {isRecording && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: 'error.main',
              animation: 'pulse 1.5s infinite',
              '@keyframes pulse': {
                '0%': {
                  opacity: 1,
                },
                '50%': {
                  opacity: 0.4,
                },
                '100%': {
                  opacity: 1,
                },
              },
            }}
          />
          <Typography variant="body2">Dictating...</Typography>
        </Box>
      )}

      {/* Finalizing… indicator with manual override.
          Shown after the user has clicked Stop while we await the backend's
          final-pass refinement (review_refining_status in_flight=false). The
          "Skip & finalize now" button lets the user bail out of a stuck final
          pass without waiting the full PENDING_STOP_TIMEOUT_MS watchdog. */}
      {isFinalizing && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="body2" color="text.secondary">
              Finalizing… waiting for server to refine transcript
            </Typography>
          </Box>
          <Button
            variant="text"
            size="small"
            color="inherit"
            onClick={forceFinalizeNow}
          >
            Skip &amp; finalize now
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AudioRecorder;